import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";

const CallForSolutionsBountyDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedArea, setSelectedArea] = useState<string>("");
  const [isBounty, setIsBounty] = useState<string | null>(null);
  const [isAreaExpanded, setIsAreaExpanded] = useState(false);
  const [isBountyExpanded, setIsBountyExpanded] = useState(false);
  const [isBountyDetailsExpanded, setIsBountyDetailsExpanded] = useState(false);
  
  // Bounty details (received from previous screen)
  const [bountyDetails, setBountyDetails] = useState<any>(null);
  
  // Detailed requirements form state
  const [callTitle, setCallTitle] = useState("");
  const [requirements, setRequirements] = useState("");
  const [additionalMaterials, setAdditionalMaterials] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  
  // Role assignment states
  const [curatorAssignment, setCuratorAssignment] = useState<string>("open");
  const [contributorAssignment, setContributorAssignment] = useState<string>("open");
  const [confirmerAssignment, setConfirmerAssignment] = useState<string>("open");
  const [composerAssignment, setComposerAssignment] = useState<string>("open");
  
  // Selected team members for roles
  const [curatorTeamMember, setCuratorTeamMember] = useState<string>("");
  const [contributorTeamMember, setContributorTeamMember] = useState<string>("");
  const [confirmerTeamMember, setConfirmerTeamMember] = useState<string>("");
  const [composerTeamMember, setComposerTeamMember] = useState<string>("");
  
  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Contact form
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  
  // Confirmation dialog state
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  // Sample team members for dropdown - Chevron team
  const teamMembers = [
    "Trey Fleming",
    "Karen Watson", 
    "Pete Dumont",
    "Giblet",
    "Chris Johnson",
    "Miguel Santos"
  ];

  // Helper functions to generate content based on call title and domain
  const generateRequirements = (title: string, description: string) => {
    const specificRequirements: { [key: string]: string } = {
      "Improving Lost Circulation Management in Unconventional Wells": `Technical Requirements:
• Comprehensive analysis of current lost circulation management practices in unconventional wells
• Development of predictive models for identifying high-risk zones prone to fluid losses
• Real-time monitoring solutions for early detection of circulation losses
• Cost-effective mitigation strategies including preventive materials and techniques
• Case studies from recent field implementations with measurable outcomes
• Training protocols for field personnel on rapid response procedures
• Integration capabilities with existing drilling management systems
• Environmental impact assessment and regulatory compliance considerations

Deliverables Expected:
• Technical report with best practices framework (50+ pages)
• Risk assessment matrix and decision-making tools
• Standard operating procedures for different well conditions
• Cost-benefit analysis of proposed solutions
• Implementation roadmap with timeline and resource requirements`,

      "Call for Lessons Learned: Kick Detection and Well Control Events": `Technical Requirements:
• Systematic collection and analysis of kick detection incidents from the past 5 years
• Development of early warning indicator systems using real-time drilling data
• Enhanced crew training protocols based on actual incident learnings
• Technology evaluation for improved kick detection sensitivity and reliability
• Integration of lessons learned into operational procedures and emergency response
• Human factors analysis to identify common causes and prevention strategies
• Regulatory compliance review and gap analysis
• Cross-industry benchmarking with safety performance metrics

Deliverables Expected:
• Comprehensive lessons learned database with searchable case studies
• Updated well control procedures incorporating latest learnings
• Training curriculum with simulation scenarios based on real events
• Technology recommendation matrix for kick detection systems
• Key performance indicators (KPIs) for monitoring well control effectiveness`,

      "Optimizing Horizontal Wellbore Cleaning Strategies": `Technical Requirements:
• Analysis of current wellbore cleaning effectiveness in horizontal drilling operations
• Development of debris removal protocols for different geological formations
• Optimization of circulation rates and fluid properties for maximum cleaning efficiency
• Advanced techniques for removing drilling cuttings from horizontal sections
• Integration of real-time monitoring for cleaning effectiveness assessment
• Cost optimization strategies without compromising operational safety
• Environmental considerations for drilling fluid management and disposal
• Technology evaluation for mechanical and chemical cleaning solutions

Deliverables Expected:
• Best practices guide for horizontal wellbore cleaning (40+ pages)
• Decision matrix for selecting appropriate cleaning strategies by formation type
• Standard operating procedures with step-by-step implementation guides
• Monitoring protocols and success metrics definition
• Cost analysis and ROI calculations for recommended solutions`
    };

    return specificRequirements[title] || description;
  };

  const generateAdditionalMaterials = (title: string, domain: string) => {
    const materialsByTitle: { [key: string]: string } = {
      "Improving Lost Circulation Management in Unconventional Wells": `Relevant Documentation and Resources:

Technical References:
• SPE 181012: "Lost Circulation Materials and Practices in Unconventional Plays"
• API RP 13B-2: "Recommended Practice for Field Testing of Oil-Based Drilling Fluids"
• IADC Drilling Manual: Chapter 8 - Lost Circulation Control

Industry Standards:
• ISO 13503-1: Drilling fluid materials specifications
• API RP 13D: Rheology and hydraulics of oil-well drilling fluids

Data Requirements:
• Historical drilling data from similar unconventional wells
• Formation pressure and fracture gradient profiles
• Drilling fluid performance records and costs
• Equipment performance data and failure analysis

Collaboration Partners:
• Halliburton - Lost circulation expertise and materials
• Baker Hughes - Drilling fluid technology and monitoring systems
• Schlumberger - Formation evaluation and real-time monitoring`,

      "Call for Lessons Learned: Kick Detection and Well Control Events": `Relevant Documentation and Resources:

Safety and Training Resources:
• IADC WellCAP certification materials and training modules
• IWCF well control certification standards and procedures
• API RP 59: Well Control Operations guidelines

Technical Standards:
• API RP 53: Recommended Practices for Blowout Prevention Equipment Systems
• NORSOK D-001: Drilling facilities standards
• ISO 16530: Well integrity for petroleum and natural gas industries

Required Data Sources:
• Historical kick detection and well control incident reports
• Real-time drilling data from kick events (pressure, flow, etc.)
• Equipment performance records during well control operations
• Training records and competency assessments

Industry Collaboration:
• National Oil Well Varco (NOV) - BOP equipment and monitoring systems
• Cameron (Schlumberger) - Well control equipment and technology
• Wild Well Control - Emergency response and specialized expertise`,

      "Optimizing Horizontal Wellbore Cleaning Strategies": `Relevant Documentation and Resources:

Technical Guidelines:
• SPE 163405: "Hole Cleaning in Horizontal Wells: A Comprehensive Approach"
• IADC/SPE 199604: "Cuttings Transport in Horizontal and Deviated Wells"
• API RP 13C: "Drilling Fluid Processing Systems Evaluation"

Engineering Standards:
• ISO 13503-2: Measurement of properties of proppants used in hydraulic fracturing
• API RP 13B-1: Recommended practice for field testing water-based drilling fluids

Required Technical Data:
• Wellbore trajectory and geological formation data
• Drilling fluid rheology and performance characteristics
• Cuttings transport efficiency measurements
• Circulation system performance and optimization data

Technology Partners:
• M-I SWACO (Schlumberger) - Drilling fluid systems and hole cleaning technology
• Newpark Drilling Fluids - Specialized drilling fluid solutions
• National Oilwell Varco - Drilling equipment and circulation systems`
    };

    return materialsByTitle[title] || `Relevant technical documentation, industry standards, and collaboration resources specific to ${title} in ${domain} operations.`;
  };

  const generateCuratorMessage = (title: string, domain: string) => {
    return `Dear Curator,

I am submitting this Call for Solutions for "${title}" as part of Chevron's ${domain} operational excellence initiative. This demonstration scenario represents a critical challenge in our ${domain.toLowerCase()} operations where innovative solutions could significantly impact our operational efficiency and safety performance.

This CfS includes a $10,000 bounty with pre-assigned team roles:
- Curator: Trey Fleming (40% attribution)
- Contributor: Karen Watson (35% attribution) 
- Confirmer: Giblet (5% attribution)
- Composer: Pete Dumont (20% attribution)

The detailed requirements and technical specifications are outlined above, including specific deliverables, technical standards, and collaboration resources. We are particularly interested in solutions that can be piloted within our existing operations and scaled across similar assets.

Please review the submission and let me know if any additional information or clarification is needed to proceed with the solution development process.

Best regards,
Trey Fleming
Senior Technical Lead, Chevron Corporation
trey.fleming@ispai.io

Note: This is a demonstration scenario showcasing the iSPAI platform's capabilities for enterprise Call for Solutions workflows.`;
  };

  // Extract the data from location state
  useEffect(() => {
    if (location.state) {
      setSelectedArea(location.state.selectedArea || "");
      setIsBounty(location.state.isBounty || null);
      
      if (location.state.bountyDetails) {
        setBountyDetails(location.state.bountyDetails);
      }
      
      // Auto-populate for Chevron demonstration calls
      if (location.state.chevronCall) {
        const chevronCall = location.state.chevronCall;
        setCallTitle(chevronCall.title || "");
        setRequirements(generateRequirements(chevronCall.title, chevronCall.description));
        setAdditionalMaterials(generateAdditionalMaterials(chevronCall.title, chevronCall.domain));
        setEmail("trey.fleming@ispai.io");
        setMessage(generateCuratorMessage(chevronCall.title, chevronCall.domain));
        
        // Set role assignments for Chevron demo
        setCuratorAssignment("assign");
        setCuratorTeamMember("Trey Fleming");
        setContributorAssignment("assign");
        setContributorTeamMember("Karen Watson");
        setConfirmerAssignment("assign");
        setConfirmerTeamMember("Giblet");
        setComposerAssignment("assign");
        setComposerTeamMember("Pete Dumont");
      }
    }
  }, [location]);

  const handleBack = () => {
    navigate("/call-for-solutions/bounty", { state: { selectedArea } });
  };

  const handleContinue = () => {
    // Navigate to the next step or submit the form
    console.log("Submitting detailed requirements:", {
      selectedArea,
      isBounty,
      bountyDetails,
      callTitle,
      requirements,
      additionalMaterials,
      selectedFiles
    });
  };

  const handleSendMessage = () => {
    // Show the confirmation dialog first
    setShowConfirmationDialog(true);
  };
  
  const handleConfirmSend = () => {
    // Process the actual sending here
    console.log("Sending message to curator:", { email, message });
    
    // Add role assignments to bounty details
    const updatedBountyDetails = {
      ...bountyDetails,
      roleAssignments: {
        curator: {
          status: curatorAssignment,
          assignedTo: curatorAssignment === "assign" ? curatorTeamMember : "Open"
        },
        contributor: {
          status: contributorAssignment,
          assignedTo: contributorAssignment === "assign" ? contributorTeamMember : "Open"
        },
        confirmer: {
          status: confirmerAssignment,
          assignedTo: confirmerAssignment === "assign" ? confirmerTeamMember : "Open"
        },
        composer: {
          status: composerAssignment,
          assignedTo: composerAssignment === "assign" ? composerTeamMember : "Open"
        }
      }
    };
    
    // Close the dialog
    setShowConfirmationDialog(false);
    
    // Clear form fields
    setEmail("");
    setMessage("");
    
    // Show success notification
    toast({
      title: "Message Sent Successfully",
      description: "Your Call for Solutions has been submitted and will appear in your mySPAI profile.",
    });
    
    // Navigate to profile or dashboard page after a delay
    setTimeout(() => {
      navigate("/myspai-profile/trey-fleming", { 
        state: { 
          newCall: {
            title: callTitle,
            area: selectedArea,
            type: isBounty === "yes" ? "Solution Bounty" : "Call for Solutions",
            date: new Date().toLocaleDateString(),
            bountyDetails: updatedBountyDetails
          }
        }
      });
    }, 2000);
  };

  const handleFileButtonClick = () => {
    // Trigger the hidden file input click event
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const totalFiles = [...selectedFiles, ...newFiles];
      
      // Limit to 5 files maximum
      if (totalFiles.length > 5) {
        toast({
          title: "Maximum Files Exceeded",
          description: "You can upload a maximum of 5 files.",
          variant: "destructive",
        });
        return;
      }
      
      setSelectedFiles(totalFiles);
    }
  };
  
  const removeFile = (index: number) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
  };
  
  // Generate the email preview for confirmation dialog
  const generateEmailPreview = () => {
    return (
      <div className="space-y-4 text-sm">
        <div>
          <p><strong>To:</strong> Curator</p>
          <p><strong>From:</strong> {email}</p>
          <p><strong>Subject:</strong> New Call for Solutions: {callTitle}</p>
        </div>
        
        <div className="border-t pt-2">
          <p className="mb-2"><strong>Call Details:</strong></p>
          <p><strong>Title:</strong> {callTitle}</p>
          <p><strong>Functional Area:</strong> {selectedArea}</p>
          <p><strong>Type:</strong> {isBounty === "yes" ? "Solution Bounty" : "Call for Solutions"}</p>
          
          {isBounty === "yes" && bountyDetails && (
            <div className="mt-2">
              <p><strong>Bounty Amount:</strong> {bountyDetails.bountyAmount || "Not specified"}</p>
              <p><strong>Bounty Type:</strong> {bountyDetails.bountyType || "Not specified"}</p>
              
              <div className="mt-2">
                <p><strong>Role Assignments:</strong></p>
                <ul className="list-disc pl-5 mt-1">
                  <li>Curator ({bountyDetails.attribution?.curatorPercentage || 0}%): {curatorAssignment === "assign" ? curatorTeamMember : "Open Call"}</li>
                  <li>Contributor ({bountyDetails.attribution?.contributorPercentage || 0}%): {contributorAssignment === "assign" ? contributorTeamMember : "Open Call"}</li>
                  <li>Confirmer ({bountyDetails.attribution?.confirmerPercentage || 0}%): {confirmerAssignment === "assign" ? confirmerTeamMember : "Open Call"}</li>
                  <li>Composer ({bountyDetails.attribution?.composerPercentage || 0}%): {composerAssignment === "assign" ? composerTeamMember : "Open Call"}</li>
                </ul>
              </div>
            </div>
          )}
          
          <div className="mt-2">
            <p><strong>Requirements:</strong></p>
            <p className="whitespace-pre-wrap">{requirements}</p>
          </div>
          
          {additionalMaterials && (
            <div className="mt-2">
              <p><strong>Additional Materials/Links:</strong></p>
              <p className="whitespace-pre-wrap">{additionalMaterials}</p>
            </div>
          )}
          
          {selectedFiles.length > 0 && (
            <div className="mt-2">
              <p><strong>Attached Files:</strong></p>
              <ul className="list-disc pl-5">
                {selectedFiles.map((file, index) => (
                  <li key={index}>{file.name}</li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="mt-4 pt-2 border-t">
            <p><strong>Message from submitter:</strong></p>
            <p className="whitespace-pre-wrap">{message}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 pb-12">
      {/* Logo */}
      <div className="mt-12 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/engage")}
        />
      </div>

      <div className="w-full max-w-2xl space-y-6">
        {/* Functional Area Summary Card */}
        <Card className="border border-gray-200 overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setIsAreaExpanded(!isAreaExpanded)}
          >
            <div>
              <h2 className="text-xl font-semibold">Select Functional Area</h2>
              <p className="text-gray-600 mt-1">Selected: {selectedArea}</p>
            </div>
            <div>
              {isAreaExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </Card>

        {/* Solution Bounty Summary Card */}
        <Card className="border border-gray-200 overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setIsBountyExpanded(!isBountyExpanded)}
          >
            <div>
              <h2 className="text-xl font-semibold">Solution Bounty</h2>
              <p className="text-gray-600 mt-1">Is this a solution bounty? {isBounty === "yes" ? "Yes" : "No"}</p>
            </div>
            <div>
              {isBountyExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </Card>

        {/* Bounty Details Summary Card */}
        {bountyDetails && (
          <Card className="border border-gray-200 overflow-hidden">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
              onClick={() => setIsBountyDetailsExpanded(!isBountyDetailsExpanded)}
            >
              <div>
                <h2 className="text-xl font-semibold">Solution Bounty Details</h2>
                <p className="text-gray-600 mt-1">
                  Amount: {bountyDetails.bountyAmount || "Not specified"}, 
                  Type: {bountyDetails.bountyType || "Not specified"}
                </p>
              </div>
              <div>
                {isBountyDetailsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
            
            {isBountyDetailsExpanded && (
              <div className="p-4 border-t border-gray-200">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Escrow Conditions</h3>
                    <p className="text-sm text-gray-600 mt-1">{bountyDetails.escrowConditions || "Not specified"}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Release Conditions</h3>
                    <p className="text-sm text-gray-600 mt-1">{bountyDetails.releaseConditions || "Not specified"}</p>
                  </div>
                  
                  {bountyDetails.attribution && (
                    <>
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Attribution Structure</h3>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          <p className="text-sm text-gray-600">Curator: {bountyDetails.attribution.curatorPercentage || 0}%</p>
                          <p className="text-sm text-gray-600">Contributor: {bountyDetails.attribution.contributorPercentage || 0}%</p>
                          <p className="text-sm text-gray-600">Confirmer: {bountyDetails.attribution.confirmerPercentage || 0}%</p>
                          <p className="text-sm text-gray-600">Composer: {bountyDetails.attribution.composerPercentage || 0}%</p>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Role Assignments</h3>
                        <div className="space-y-4 mt-3">
                          {/* Curator Assignment */}
                          <div className="space-y-2">
                            <p className="text-sm text-gray-700">Curator ({bountyDetails.attribution.curatorPercentage || 0}%)</p>
                            <div className="flex space-x-3">
                              <div className="flex items-center">
                                <input 
                                  type="radio" 
                                  id="curator-assign" 
                                  name="curator-assignment" 
                                  value="assign"
                                  checked={curatorAssignment === "assign"}
                                  onChange={() => setCuratorAssignment("assign")}
                                  className="mr-2" 
                                />
                                <label htmlFor="curator-assign" className="text-sm">Assign</label>
                              </div>
                              <div className="flex items-center">
                                <input 
                                  type="radio" 
                                  id="curator-open" 
                                  name="curator-assignment" 
                                  value="open"
                                  checked={curatorAssignment === "open"}
                                  onChange={() => setCuratorAssignment("open")}
                                  className="mr-2" 
                                />
                                <label htmlFor="curator-open" className="text-sm">Open Call</label>
                              </div>
                            </div>
                            
                            {curatorAssignment === "assign" && (
                              <Select onValueChange={setCuratorTeamMember} value={curatorTeamMember}>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select team member" />
                                </SelectTrigger>
                                <SelectContent>
                                  {teamMembers.map(member => (
                                    <SelectItem key={member} value={member}>
                                      {member}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                          
                          {/* Contributor Assignment */}
                          <div className="space-y-2">
                            <p className="text-sm text-gray-700">Contributor ({bountyDetails.attribution.contributorPercentage || 0}%)</p>
                            <div className="flex space-x-3">
                              <div className="flex items-center">
                                <input 
                                  type="radio" 
                                  id="contributor-assign" 
                                  name="contributor-assignment" 
                                  value="assign"
                                  checked={contributorAssignment === "assign"}
                                  onChange={() => setContributorAssignment("assign")}
                                  className="mr-2" 
                                />
                                <label htmlFor="contributor-assign" className="text-sm">Assign</label>
                              </div>
                              <div className="flex items-center">
                                <input 
                                  type="radio" 
                                  id="contributor-open" 
                                  name="contributor-assignment" 
                                  value="open"
                                  checked={contributorAssignment === "open"}
                                  onChange={() => setContributorAssignment("open")}
                                  className="mr-2" 
                                />
                                <label htmlFor="contributor-open" className="text-sm">Open Call</label>
                              </div>
                            </div>
                            
                            {contributorAssignment === "assign" && (
                              <Select onValueChange={setContributorTeamMember} value={contributorTeamMember}>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select team member" />
                                </SelectTrigger>
                                <SelectContent>
                                  {teamMembers.map(member => (
                                    <SelectItem key={member} value={member}>
                                      {member}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                          
                          {/* Confirmer Assignment */}
                          <div className="space-y-2">
                            <p className="text-sm text-gray-700">Confirmer ({bountyDetails.attribution.confirmerPercentage || 0}%)</p>
                            <div className="flex space-x-3">
                              <div className="flex items-center">
                                <input 
                                  type="radio" 
                                  id="confirmer-assign" 
                                  name="confirmer-assignment" 
                                  value="assign"
                                  checked={confirmerAssignment === "assign"}
                                  onChange={() => setConfirmerAssignment("assign")}
                                  className="mr-2" 
                                />
                                <label htmlFor="confirmer-assign" className="text-sm">Assign</label>
                              </div>
                              <div className="flex items-center">
                                <input 
                                  type="radio" 
                                  id="confirmer-open" 
                                  name="confirmer-assignment" 
                                  value="open"
                                  checked={confirmerAssignment === "open"}
                                  onChange={() => setConfirmerAssignment("open")}
                                  className="mr-2" 
                                />
                                <label htmlFor="confirmer-open" className="text-sm">Open Call</label>
                              </div>
                            </div>
                            
                            {confirmerAssignment === "assign" && (
                              <Select onValueChange={setConfirmerTeamMember} value={confirmerTeamMember}>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select team member" />
                                </SelectTrigger>
                                <SelectContent>
                                  {teamMembers.map(member => (
                                    <SelectItem key={member} value={member}>
                                      {member}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                          
                          {/* Composer Assignment */}
                          <div className="space-y-2">
                            <p className="text-sm text-gray-700">Composer ({bountyDetails.attribution.composerPercentage || 0}%)</p>
                            <div className="flex space-x-3">
                              <div className="flex items-center">
                                <input 
                                  type="radio" 
                                  id="composer-assign" 
                                  name="composer-assignment" 
                                  value="assign"
                                  checked={composerAssignment === "assign"}
                                  onChange={() => setComposerAssignment("assign")}
                                  className="mr-2" 
                                />
                                <label htmlFor="composer-assign" className="text-sm">Assign</label>
                              </div>
                              <div className="flex items-center">
                                <input 
                                  type="radio" 
                                  id="composer-open" 
                                  name="composer-assignment" 
                                  value="open"
                                  checked={composerAssignment === "open"}
                                  onChange={() => setComposerAssignment("open")}
                                  className="mr-2" 
                                />
                                <label htmlFor="composer-open" className="text-sm">Open Call</label>
                              </div>
                            </div>
                            
                            {composerAssignment === "assign" && (
                              <Select onValueChange={setComposerTeamMember} value={composerTeamMember}>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select team member" />
                                </SelectTrigger>
                                <SelectContent>
                                  {teamMembers.map(member => (
                                    <SelectItem key={member} value={member}>
                                      {member}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Detailed Requirements Card */}
        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-2">Detailed Requirements</h2>
            <p className="text-gray-600 mb-4">Specify the requirements and provide any additional materials</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Call Title</label>
                <Input 
                  placeholder="Enter a title for this Call for Solutions..." 
                  value={callTitle}
                  onChange={(e) => setCallTitle(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label>
                <Textarea 
                  placeholder="Describe in detail what needs to be done..." 
                  className="min-h-[100px]"
                  value={requirements}
                  onChange={(e) => setRequirements(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Materials/Links</label>
                <Textarea 
                  placeholder="Add any relevant links or materials..." 
                  className="min-h-[100px]"
                  value={additionalMaterials}
                  onChange={(e) => setAdditionalMaterials(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Files (Max 5)</label>
                <div className="flex items-center mt-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    multiple
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-sm"
                    onClick={handleFileButtonClick}
                    disabled={selectedFiles.length >= 5}
                  >
                    Choose Files
                  </Button>
                  <span className="ml-2 text-sm text-gray-500">
                    {selectedFiles.length > 0 
                      ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected` 
                      : "No files chosen"}
                  </span>
                </div>
                
                {selectedFiles.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Selected Files:</p>
                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                          <span className="text-sm truncate max-w-xs">{file.name}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0" 
                            onClick={() => removeFile(index)}
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Curator Card */}
        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-2">Contact Curator</h2>
            <p className="text-gray-600 mb-4">Have questions? Send a message to the curator</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message to Curator</label>
                <Textarea 
                  placeholder="Enter your message or question for the curator..." 
                  className="min-h-[100px]"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              
              <Button 
                className="w-full bg-[#14213d] hover:bg-[#0d1829] text-white"
                onClick={handleSendMessage}
                disabled={!email || !message || !callTitle}
              >
                Send Message
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mb-12">
          <Button 
            variant="outline" 
            onClick={handleBack}
            className="border-gray-300"
          >
            Back
          </Button>
          
          <Button 
            onClick={handleContinue}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
          >
            Continue
          </Button>
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmationDialog} onOpenChange={setShowConfirmationDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confirm Call for Solutions Submission</DialogTitle>
            <DialogDescription>
              The following message will be sent. After submission, this Call for Solutions will appear in your mySPAI profile.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 border rounded-md p-4 bg-gray-50">
            {generateEmailPreview()}
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowConfirmationDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#14213d] hover:bg-[#0d1829] text-white"
              onClick={handleConfirmSend}
            >
              Confirm & Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallForSolutionsBountyDetails;